import { Link, useNavigate, useParams } from "react-router";
import { toast } from "sonner";
import { IconChevronLeft } from "@tabler/icons-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useBoat } from "@/api/hooks/use-boats";
import { usePlatformWeighings } from "@/api/hooks/use-platform-weighings";
import { usePlatformWeighingActions } from "@/hooks/use-platform-weighing-actions";
import { PlatformWeighingForm, type PendingCapture } from "@/components/platform-weighing-form";
import { saveWeighingThenMaybeSignOff } from "@/lib/weighing-save";
import { enqueueUpload } from "@/lib/upload-store";
import { WeighingPhotosSection } from "@/components/weighing-photos-section";
import { WeighingStateBadge } from "@/components/weighing-state-badge";
import { asBoatId, type PlatformWeighingId } from "@/types/identifiers";
import type { components } from "@/api/generated/schema";

type PlatformWeighingCreate = components["schemas"]["PlatformWeighingCreate"];
type PlatformWeighingUpdate = components["schemas"]["PlatformWeighingUpdate"];

export function PlatformWeighingPage() {
  const params = useParams();
  const navigate = useNavigate();
  const boatId = asBoatId(params.boatId ?? "");
  const weighingId = params.weighingId;

  const boatQuery = useBoat(boatId);
  const listQuery = usePlatformWeighings(boatId);
  const actions = usePlatformWeighingActions(boatId, listQuery.data);

  const backTo = `/boats/${boatId}/weighings?tab=platform`;
  const isNew = !weighingId;
  const weighing = isNew ? undefined : listQuery.data?.find((w) => w.id === weighingId);
  const boatCode = boatQuery.data?.boat_code ?? "—";

  // The list doubles as the form's data source: initialData for edit/view and
  // the newest id for the create-draft version check.
  if (listQuery.isLoading || boatQuery.isLoading) {
    return (
      <div className="max-w-3xl mx-auto">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-96 w-full rounded-lg" />
      </div>
    );
  }

  if (!isNew && !weighing) {
    return (
      <div className="max-w-3xl mx-auto">
        <p className="text-sm text-muted-foreground mb-3">Weighing not found.</p>
        <Link to={backTo} className="text-sm text-foreground underline">
          Back to {boatCode} weighings
        </Link>
      </div>
    );
  }

  const readOnly = !!weighing && weighing.state !== "draft";

  const handleSave = async (
    payload: PlatformWeighingCreate | PlatformWeighingUpdate | null,
    withSignOff: boolean,
    overrideGate = false,
    captures: PendingCapture[] = [],
  ) => {
    // Form captures become weighing photos via the tray. Before a sign-off
    // they must land first (the photo draft-gate closes at sign-off), so the
    // sign-off closure awaits the tray jobs and bails on any failure — the
    // draft then stays on the list with the failed job retryable in the tray.
    const id = await saveWeighingThenMaybeSignOff({
      payload,
      withSignOff,
      existingId: weighing?.id as PlatformWeighingId | undefined,
      update: actions.handleUpdate,
      create: actions.handleCreate,
      signOffAsync: async (id) => {
        if (captures.length > 0) {
          const jobs = captures.map((c) => enqueueUpload({ weighingId: id, ...c }));
          const landed = await Promise.all(jobs.map((j) => j.done));
          if (!landed.every(Boolean)) {
            toast.error(
              "Draft saved, but a capture didn't upload — retry it in the tray, then sign off from the list.",
            );
            return;
          }
        }
        await actions.handleSignOffAsync(id, overrideGate);
      },
    });
    if (id && !withSignOff) {
      for (const c of captures) enqueueUpload({ weighingId: id, ...c });
    }
  };

  return (
    <div className="max-w-3xl lg:max-w-5xl mx-auto">
      <div className="mb-4">
        <Link
          to={backTo}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2 w-fit"
        >
          <IconChevronLeft className="w-3 h-3" /> {boatCode} weighings
        </Link>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold">
            {isNew ? "New platform weighing" : "Platform weighing"} — {boatCode}
          </h1>
          {weighing && <WeighingStateBadge state={weighing.state} />}
        </div>
        {weighing && (
          <p className="text-sm text-muted-foreground">
            Measured {new Date(weighing.measured_at).toLocaleDateString()} by {weighing.measured_by}
          </p>
        )}
      </div>

      <PlatformWeighingForm
        key={weighing?.id ?? "new"}
        boatId={boatId}
        initialData={weighing}
        latestWeighingId={listQuery.data?.[0]?.id ?? null}
        readOnly={readOnly}
        isSubmitting={actions.isCreating || actions.isUpdating}
        isSigningOff={actions.isSigningOff}
        onSave={handleSave}
        onDone={() => navigate(backTo)}
        onUnlock={
          weighing?.state === "signed_off"
            ? () => {
                if (window.confirm("Unlock for editing? This will move it back to draft.")) {
                  actions.handleUnlock(weighing.id as PlatformWeighingId);
                }
              }
            : undefined
        }
        isUnlocking={actions.isUnlocking}
      />

      {/* Photos attach to the weighing row, so a brand-new weighing must be
          saved as a draft before evidence can be captured. */}
      <div className="mt-8">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Weighing photos
        </div>
        {weighing ? (
          <WeighingPhotosSection
            weighingId={weighing.id as PlatformWeighingId}
            readOnly={readOnly}
          />
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">
            Save the draft to start attaching weighing photos.
          </p>
        )}
      </div>
    </div>
  );
}
